/* ============================================================================
   ¿ESTOY CORRIENDO CÓDIGO VIEJO?

   POR QUÉ EXISTE

   Durante días, el iPad mostró una versión de la aplicación de semanas atrás.
   Se compilaba, se servía, se entregaba — y en el iPad no cambiaba nada. La
   causa: un service worker instalado antes seguía sirviendo la aplicación
   desde su caché.

   Y no se arreglaba solo, por dos motivos que se suman:

   1. Un service worker nuevo **no toma el control hasta que se cierran TODAS
      las pestañas** del sitio. Con dos abiertas, eso no ocurre nunca.
   2. El instalado en ese iPad era de cuando la aplicación esperaba
      confirmación antes de actualizar, así que ni siquiera lo intentaba.

   Se perdieron muchas rondas confundiendo «esto no funciona» con «el navegador
   está corriendo otra cosa». Desde afuera son indistinguibles.

   CÓMO FUNCIONA

   El servidor publica en `/sello` la fecha y hora de la compilación que está
   sirviendo. La aplicación compara con la suya. Si no coinciden, el navegador
   está corriendo código viejo: se da de baja el service worker, se vacían las
   cachés y se recarga UNA vez.

   Una recarga sola y automática es mucho menos grave que una aplicación que
   miente sobre su versión durante semanas.
   ========================================================================== */

const CLAVE_RECARGA = "hcRecargaPorSelloViejo";

/** El sello de esta compilación, inyectado por Vite. */
export const SELLO_PROPIO = typeof __SELLO_BUILD__ === "string" ? __SELLO_BUILD__ : "desarrollo";

/**
 * Compara los dos sellos.
 *
 * Devuelve `null` cuando no se puede saber —sin red, servidor sin el endpoint,
 * modo desarrollo—. En la duda NO se recarga: una recarga en bucle es peor que
 * una versión vieja.
 */
export function estaDesactualizado(selloPropio, selloDelServidor) {
  if (!selloDelServidor || typeof selloDelServidor !== "string") return null;
  if (!selloPropio || selloPropio === "desarrollo" || selloPropio === "test") return null;
  return selloPropio !== selloDelServidor;
}

/**
 * Da de baja los service workers y vacía las cachés.
 *
 * Ambas cosas: dar de baja el service worker no borra lo que ya guardó, y una
 * caché vieja vuelve a servir el mismo código en la próxima carga.
 */
export async function limpiarTodo(entorno = globalThis) {
  const hechos = { serviceWorkers: 0, caches: 0 };

  try {
    const registros = await entorno.navigator?.serviceWorker?.getRegistrations?.();
    for (const r of registros || []) {
      await r.unregister();
      hechos.serviceWorkers += 1;
    }
  } catch { /* sin service workers no hay nada que dar de baja */ }

  try {
    const nombres = await entorno.caches?.keys?.();
    for (const nombre of nombres || []) {
      await entorno.caches.delete(nombre);
      hechos.caches += 1;
    }
  } catch { /* sin API de cachés tampoco */ }

  return hechos;
}

/**
 * Comprueba el sello y se corrige si hace falta.
 *
 * @returns {Promise<{estado: string, propio: string, servidor: string|null}>}
 */
export async function comprobarSello(entorno = globalThis) {
  const propio = SELLO_PROPIO;

  let selloDelServidor = null;
  try {
    /* `no-store` es obligatorio: se está tratando de saltear justamente la
       caché, así que pedirlo de la forma normal traería la respuesta vieja. */
    const r = await entorno.fetch("/sello", { cache: "no-store" });
    if (r.ok) selloDelServidor = (await r.json())?.sello ?? null;
  } catch {
    return { estado: "sin-respuesta", propio, servidor: null };
  }

  const viejo = estaDesactualizado(propio, selloDelServidor);
  if (viejo === null) return { estado: "no-comparable", propio, servidor: selloDelServidor };
  if (viejo === false) return { estado: "al-dia", propio, servidor: selloDelServidor };

  /* Una sola recarga. La marca en `sessionStorage` evita el bucle: si después
     de limpiar y recargar el sello SIGUE sin coincidir —por ejemplo porque hay
     un proxy en el medio— la aplicación se queda como está en vez de recargar
     para siempre. */
  let yaSeIntento = false;
  try { yaSeIntento = entorno.sessionStorage?.getItem(CLAVE_RECARGA) === propio; } catch { /* modo privado */ }
  if (yaSeIntento) return { estado: "viejo-sin-arreglo", propio, servidor: selloDelServidor };

  try { entorno.sessionStorage?.setItem(CLAVE_RECARGA, propio); } catch { /* modo privado */ }

  await limpiarTodo(entorno);
  entorno.location?.reload?.();
  return { estado: "limpiado", propio, servidor: selloDelServidor };
}
